import { useCallback } from "react";
import type { Task } from "@common/types/task";
import { PresenceStack } from "@components/avatar/PresenceStack";
import { useAgentRunForTask } from "@components/presence/useAgentRunForTask";
import { usePresenceUsers } from "@components/presence/usePresenceUsers";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useTaskPresence } from "@hooks/presence/useTaskPresence";
import { TaskAssigneeControl } from "../board/TaskBoardFieldControls";
import {
    getTaskCardEditPatch,
    isTaskFieldEditable,
    type TaskCardEdit,
} from "../board/taskCardEdits";
import { getTaskPresenceMembers } from "../board/taskPresenceMembers";
import { useTaskBoardSave } from "../board/useTaskBoardSave";
import { useTaskThreadParticipants } from "./useTaskThreadParticipants";

/** Saves an assignee pick like a board card does: the new avatar shows at once, a failed save rolls back. */
const useSaveTaskAssignee = () => {
    const { applyUpdate, save } = useTaskBoardSave("Failed to update task");
    return useCallback(
        ({ task, body, assignee }: TaskCardEdit) =>
            save(
                applyUpdate({
                    task,
                    body,
                    patch: getTaskCardEditPatch({ body, assignee }),
                }),
            ),
        [applyUpdate, save],
    );
};

/**
 * Who is on the open task, as on its board card: Kick, the other viewers,
 * the people who posted in its chat and the assignee. As on the card, the
 * assignee avatar (or the empty slot) opens the assignee picker, unless the
 * user is view-only or the task can't change its assignee. Also announces
 * that the current user is viewing it.
 */
export const TaskBodyPeople = ({ task }: { task: Task }) => {
    const viewerIds = useTaskPresence({
        taskId: task.id,
        closeId: task.closeId,
    });
    const viewers = usePresenceUsers(viewerIds);
    const contributors = useTaskThreadParticipants(task.thread?.id);
    const { isWorking: isAgentWorking } = useAgentRunForTask(task.id);
    const { isViewOnly } = useViewOnlyRefusal();
    const saveAssignee = useSaveTaskAssignee();

    const members = getTaskPresenceMembers({
        isAgentWorking,
        viewers,
        assignee: task.assignee ?? null,
        contributors,
    });

    if (isViewOnly || !isTaskFieldEditable({ task, field: "assignee" })) {
        return <PresenceStack size="md" members={members} />;
    }

    return (
        <TaskAssigneeControl
            editor={{ task, onEdit: saveAssignee }}
            renderTrigger={(props) => (
                <PresenceStack
                    size="md"
                    members={members}
                    assigneeButtonProps={props}
                />
            )}
        />
    );
};
